/**
 * Mobile News Detail Component
 * 
 * A mobile-optimized news article detail page with touch-friendly interface.
 * Shows full article content with mobile-specific layout and navigation.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import apiHelper from '../../utils/apiHelper';
import { formatDate, cleanJson, addTargetBlankToLinks, removeFirstBoldTag, decodeDynamicsTrackingUrl } from '../../utils/textUtils';
import './MobileNewsDetail.css';

const { FILE_NAMES } = require('../../utils/constants');

/**
 * Mobile News Detail Component
 * 
 * @returns {JSX.Element} Mobile news detail page
 */
const MobileNewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleImage, setArticleImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  
  // Media states
  const [videoSource, setVideoSource] = useState(null);
  const [podcastSource, setPodcastSource] = useState(null);
  const [pdfSource, setPdfSource] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
  const [isPodcastEmbedded, setIsPodcastEmbedded] = useState(false);
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Get article from state or fetch from API
  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        
        // First try to get article from navigation state
        const articleFromState = location.state?.currentNews;
        
        if (articleFromState) {
          // Check if the article matches the ID
          const articleId = articleFromState.id || articleFromState.itemId;
          const currentId = parseInt(id);
          
          if (articleId === currentId || articleId === parseInt(currentId) || String(articleId) === String(currentId)) {
            setArticle(articleFromState);
            setLoading(false);
            return;
          }
        }
        
        // If not found in state, fetch from API
        const newsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MONTHLY_NEWS);
        
        // Use robust ID matching logic (same as desktop News component)
        const foundArticle = newsResult.fileContent.find(item => {
          const itemId = item.id;
          
          // Check multiple ID match types
          if (itemId === id) {
            return true;
          } else if (itemId === parseInt(id)) {
            return true;
          } else if (String(itemId) === String(id)) {
            return true;
          }
          
          return false;
        });
        
        if (foundArticle) {
          setArticle(foundArticle);
        } else {
          setError('Article not found');
          console.error('Article not found for ID:', id);
          console.error('Available article IDs:', newsResult.fileContent.slice(0, 10).map(item => ({
            id: item.id,
            type: typeof item.id,
            title: item.title?.substring(0, 50) + '...'
          })));
        }
      } catch (err) {
        setError('Error loading article: ' + err.message);
        console.error('Error fetching article:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id, location.state]);

  // Handle escape key to close expanded video
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isVideoExpanded) {
        setIsVideoExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVideoExpanded]);

  // Load article image (same logic as desktop components)
  useEffect(() => {
    const loadArticleImage = async () => {
      // Check for various image property names that might exist
      const imageProperty = article?.image || article?.picture || article?.imageUrl;
      
      if (!imageProperty) {
        setArticleImage(getRandomImage());
        setImageLoading(false);
        return;
      }

      // Check if it's already a blob URL
      if (imageProperty.startsWith('blob:')) {
        setArticleImage(imageProperty);
        setImageLoading(false);
        return;
      }

      try {
        // Check if it's a direct image URL
        if (imageProperty.includes('assets/img/news/weekly/')) {
          setArticleImage(imageProperty);
          setImageLoading(false);
          return;
        }

        if (imageProperty.includes('https://')) {
          setArticleImage(imageProperty);
          setImageLoading(false);
          return;
        }

        // For local images, use authentication
        const authToken = await apiHelper.getAuthToken();
        const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${imageProperty}`;
        
        const response = await fetch(imageUrl, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const imageObjectUrl = URL.createObjectURL(blob);
          setArticleImage(imageObjectUrl);
        } else {
          // Fallback to random image if fetch fails
          setArticleImage(getRandomImage());
        }
      } catch (error) {
        console.error('Error loading article image:', error);
        // Fallback to random image on error
        setArticleImage(getRandomImage());
      } finally {
        setImageLoading(false);
      }
    };

    if (article) {
      loadArticleImage();
    }
  }, [article]);

  // Helper functions for media URLs
  const getEmbeddableVideoUrl = (url) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1].split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const getEmbeddablePodcastUrl = (url) => {
    if (url.includes('spotify.com/episode/')) {
      const episodeId = url.split('episode/')[1].split('?')[0];
      return `https://open.spotify.com/embed/episode/${episodeId}`;
    } else if (url.includes('spotify.com/show/')) {
      const showId = url.split('show/')[1].split('?')[0];
      return `https://open.spotify.com/embed/show/${showId}`;
    } else if (url.includes('soundcloud.com/')) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
    } else if (url.includes('anchor.fm/')) {
      return url.replace('anchor.fm/', 'anchor.fm/embed/');
    }
    return url;
  };

  // Load video content
  useEffect(() => {
    const loadVideo = async () => {
      if (!article?.video || !article.video.trim()) return;

      setIsVideoLoading(true);
      
      if (article.video.includes('https://')) {
        const isYouTube = article.video.includes('youtube.com') || article.video.includes('youtu.be');
        const isVimeo = article.video.includes('vimeo.com');

        if (isYouTube || isVimeo) {
          setVideoSource(getEmbeddableVideoUrl(article.video));
          setIsVideoEmbedded(true);
        } else {
          setVideoSource(article.video);
          setIsVideoEmbedded(false);
        }
        setIsVideoLoading(false);
      } else {
        // For local videos, try streaming endpoint first
        try {
          const streamingUrl = `${process.env.REACT_APP_API_URL}media/stream/${article.video}`;
          const testResponse = await fetch(streamingUrl, { 
            method: 'HEAD',
            headers: { 'Range': 'bytes=0-1' }
          });
          
          if (testResponse.ok || testResponse.status === 206) {
            setVideoSource(streamingUrl);
            setIsVideoEmbedded(false);
          } else {
            // Fallback to blob download
            const { fileContent } = await apiHelper.fetchVideoByName(article.video);
            const blobUrl = URL.createObjectURL(fileContent);
            setVideoSource(blobUrl);
            setIsVideoEmbedded(false);
          }
        } catch (error) {
          try {
            const { fileContent } = await apiHelper.fetchVideoByName(article.video);
            const blobUrl = URL.createObjectURL(fileContent);
            setVideoSource(blobUrl);
            setIsVideoEmbedded(false);
          } catch (blobError) {
            console.error('Error loading video:', blobError);
          }
        }
        setIsVideoLoading(false);
      }
    };

    loadVideo();
  }, [article]);

  // Load podcast/audio content
  useEffect(() => {
    const loadPodcast = async () => {
      if ((!article?.podcast || !article.podcast.trim()) && (!article?.audio || !article.audio.trim())) return;

      const podcastUrl = article.podcast || article.audio;
      setIsAudioLoading(true);

      if (podcastUrl.includes('https://')) {
        const isSpotify = podcastUrl.includes('spotify.com');
        const isSoundCloud = podcastUrl.includes('soundcloud.com');
        const isAnchor = podcastUrl.includes('anchor.fm');
        const isPodLink = podcastUrl.includes('pod.link');

        if (isSpotify || isSoundCloud || isAnchor || isPodLink) {
          setPodcastSource(getEmbeddablePodcastUrl(podcastUrl));
          setIsPodcastEmbedded(true);
          setTimeout(() => setIsAudioLoading(false), 5000);
        } else {
          setPodcastSource(podcastUrl);
          setIsPodcastEmbedded(false);
          setIsAudioLoading(false);
        }
      } else {
        try {
          const authToken = await apiHelper.getAuthToken();
          const audioUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${podcastUrl}`;
          
          const response = await fetch(audioUrl, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          
          if (!response.ok) throw new Error('Failed to fetch audio');
          
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setPodcastSource(objectUrl);
          setIsPodcastEmbedded(false);
          setIsAudioLoading(false);
        } catch (err) {
          console.error('Error loading podcast:', err);
          setIsAudioLoading(false);
        }
      }
    };

    loadPodcast();
  }, [article]);

  // Load PDF content
  useEffect(() => {
    const loadPdf = async () => {
      if (!article?.file || !article.file.trim()) return;

      if (article.file.includes('https://')) {
        const isPdfFile = article.file.toLowerCase().includes('.pdf') ||
          article.file.toLowerCase().includes('pdf') ||
          article.file.includes('application/pdf');

        if (isPdfFile) {
          setPdfSource(article.file);
        } else {
          setPdfSource(null);
        }
      } else {
        try {
          let response;
          try {
            response = await apiHelper.fetchPDFNameAndContentByTitle(article.file);
          } catch (err) {
            const authToken = await apiHelper.getAuthToken();
            const pdfUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${article.file}`;

            const fetchResponse = await fetch(pdfUrl, {
              headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (!fetchResponse.ok) throw new Error('Failed to fetch PDF');

            const blob = await fetchResponse.blob();
            response = { fileContent: blob };
          }

          if (response?.fileContent) {
            const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
            const pdfBlobUrl = URL.createObjectURL(pdfBlob);
            setPdfSource(pdfBlobUrl);
          } else {
            throw new Error('No PDF content received');
          }
        } catch (err) {
          console.error('Error loading PDF:', err);
          setPdfSource(null);
        }
      }
    };

    loadPdf();
    
    // Cleanup function to revoke blob URLs
    return () => {
      if (pdfSource && pdfSource.startsWith('blob:')) {
        URL.revokeObjectURL(pdfSource);
      }
      if (videoSource && videoSource.startsWith('blob:')) {
        URL.revokeObjectURL(videoSource);
      }
      if (podcastSource && podcastSource.startsWith('blob:')) {
        URL.revokeObjectURL(podcastSource);
      }
    };
  }, [article]);

  // Get category color
  const getCategoryColor = (category) => {
    if (!category) return 'ifia-green';
    
    const normalizedCategory = category.replace(/&/g, "").replace(/\s+/g, "");
    const colorMap = {
      'Intro': 'ifia-dark-green',
      'AdvocacyRegulatory': 'ifia-green',
      'Events': 'ifia-orange',
      'EngagementsCommunications': 'ifia-orange',
      'EngagementCommunications': 'ifia-purple',
      'CapabilityInnovation': 'ifia-dark-green',
      'WorkingGroupUpdates': 'ifia-blue',
      'Promoted': 'ifia-purple',
      'Featured': 'ifia-purple'
    };
    return colorMap[normalizedCategory] || 'ifia-green';
  };

  // Get random fallback image
  const getRandomImage = () => {
    const randomImages = [
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop',
      'https://images.unsplash.com/photo-1444653614773-995cb1ef5bce?w=300&h=200&fit=crop',
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300&h=200&fit=crop',
      'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=300&h=200&fit=crop',
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop'
    ];
    return randomImages[Math.floor(Math.random() * randomImages.length)];
  };

  // Helper function to check if description has meaningful content (same as NewsModal.js)
  const hasDescriptionContent = (description, title) => {
    if (!description) return false;

    // Helper function to normalize apostrophes and quotes (same as in textUtils)
    const normalizeText = (text) => {
      return text
        .replace(/[''`´]/g, "'")  // Normalize different apostrophe types
        .replace(/\u2019/g, "'") // Unicode right single quotation mark (8217)
        .replace(/[""]/g, '"')   // Normalize different quote types
        .replace(/\u0027/g, "'") // Unicode apostrophe
        .replace(/&#39;/g, "'")  // HTML entity apostrophe
        .replace(/&apos;/g, "'") // XML apostrophe entity
        .toLowerCase()
        .trim();
    };

    // Create a temporary div to process the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = addTargetBlankToLinks(cleanJson(description));

    // Check if title exists and matches content
    if (title) {
      const titleWords = normalizeText(title.split(" ").slice(0, 3).join(" "));

      // Find all bold elements and check if any contain the title
      const boldElements = tempDiv.querySelectorAll('b, strong, span[style*="font-weight: bold"], span[style*="font-weight:bold"], strong[style], p strong, p[style*="font-weight: bold"]');

      let titleFound = false;
      boldElements.forEach((boldElement) => {
        if (!titleFound) {
          const boldText = normalizeText(boldElement.textContent || '');
          const boldWords = boldText.split(" ").slice(0, 3).join(" ");

          // Check if this bold element contains the title
          const isMatch = boldText.includes(titleWords) ||
            titleWords.includes(boldText) ||
            boldWords === titleWords;

          if (isMatch) {
            titleFound = true;

            // Find the appropriate parent to remove
            let elementToRemove = boldElement;

            // If strong is inside a p tag, consider removing the p tag
            if (boldElement.tagName === 'STRONG' && boldElement.parentElement && boldElement.parentElement.tagName === 'P') {
              const pElement = boldElement.parentElement;
              const pContent = pElement.innerHTML.replace(boldElement.outerHTML, '').trim();
              if (pContent === '' || pContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                elementToRemove = pElement;
              }
            }

            // If the element to remove is inside a div that only contains this element, remove the div instead
            if (elementToRemove.parentElement && elementToRemove.parentElement.tagName === 'DIV') {
              const divElement = elementToRemove.parentElement;
              const divContent = divElement.innerHTML.replace(elementToRemove.outerHTML, '').trim();
              if (divContent === '' || divContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                elementToRemove = divElement;
              }
            }

            elementToRemove.remove();
          }
        }
      });
    }

    // Clean up empty p tags
    const emptyParagraphs = tempDiv.querySelectorAll('p');
    emptyParagraphs.forEach(p => {
      const content = p.innerHTML.trim();
      if (content === '<br>' || content === '<br/>' || content === '<br />' ||
        content === '&nbsp;' || content === '' || content === ' ' ||
        content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
        p.remove();
      }
    });

    // Clean up empty div tags as well
    const emptyDivs = tempDiv.querySelectorAll('div');
    emptyDivs.forEach(div => {
      const content = div.innerHTML.trim();
      if (content === '<br>' || content === '<br/>' || content === '<br />' ||
        content === '&nbsp;' || content === '' || content === ' ' ||
        content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
        div.remove();
      }
    });

    // Check if there's meaningful content left
    const finalTextContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
    return finalTextContent.length > 0;
  };

  // Preprocess content to convert "text [url]" format to proper HTML links
  const preprocessContentLinks = (content) => {
    if (!content || typeof content !== 'string') return '';
    
    // First, convert bullet points to proper HTML lists
    content = content.replace(/^\s*\*\s+(.+)$/gm, '<li>$1</li>');
    
    // Wrap consecutive list items in <ul> tags
    content = content.replace(/(<li>.*?<\/li>(\s*<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');
    
    // Convert standalone URLs in square brackets to proper HTML links
    // This handles cases like: "Some Title\n\n[https://example.com]"
    const standaloneUrlPattern = /\n?\s*\[(https?:\/\/[^\]]+)\]\s*\n?/g;
    
    content = content.replace(standaloneUrlPattern, (match, url) => {
      // Decode Microsoft Dynamics tracking URLs before creating the link
      const cleanedUrl = decodeDynamicsTrackingUrl(url.trim());
      
      // Create proper HTML anchor tag with a meaningful link text
      const linkText = cleanedUrl.includes('iob.ie') ? 'View Programme Details' : 'Learn More';
      return `<br><a href="${cleanedUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a><br>`;
    });
    
    // Also handle the original pattern for "text [url]" format (keeping for backwards compatibility)
    const linkPattern = /(.+?)\s*\[(https?:\/\/[^\]]+)\]/g;
    
    content = content.replace(linkPattern, (match, text, url) => {
      // Clean up the text
      let linkText = text.trim();
      
      // Remove trailing punctuation if it exists
      linkText = linkText.replace(/[.,;:!?]+$/, '');
      
      // If text is empty, use a generic "Link" text
      if (!linkText || linkText.length < 2) {
        linkText = 'Link';
      }
      
      // Decode Microsoft Dynamics tracking URLs before creating the link
      const cleanedUrl = decodeDynamicsTrackingUrl(url.trim());
      
      // Create proper HTML anchor tag
      return `${linkText} - <a href="${cleanedUrl}" target="_blank" rel="noopener noreferrer">View Details</a>`;
    });
    
    // Clean up any extra spacing around HTML tags
    content = content.replace(/\s*<\/li>\s*<li>\s*/g, '</li><li>');
    content = content.replace(/\s*<\/ul>\s*<ul>\s*/g, '</ul><ul>');
    
    // Convert line breaks to proper HTML
    content = content.replace(/\n\n/g, '</p><p>');
    content = content.replace(/\n/g, '<br>');
    
    // Wrap in paragraph tags if not already wrapped and doesn't start with list
    if (!content.startsWith('<p>') && !content.startsWith('<ul>') && !content.startsWith('<h') && !content.startsWith('<div')) {
      content = `<p>${content}</p>`;
    }
    
    return content;
  };

  if (loading) {
    return (
      <MobileLayout title="Loading..." showBackButton={true}>
        <div className="mobile-news-detail-loading">
          <div className="mobile-loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>Loading article...</p>
        </div>
      </MobileLayout>
    );
  }

  if (error || !article) {
    return (
      <MobileLayout title="Error" showBackButton={true}>
        <div className="mobile-news-detail-error">
          <div className="mobile-error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3>Article Not Found</h3>
          <p>{error || 'The requested article could not be found.'}</p>
          <button 
            className="mobile-back-to-news-btn"
            onClick={() => navigate('/mobile/news')}
          >
            <i className="fas fa-arrow-left"></i>
            Back to News
          </button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title="Article" showBackButton={true}>
      <div className="mobile-news-detail">
        {/* Article Image */}
        <div className="mobile-news-detail-image">
          {imageLoading ? (
            <div className="mobile-image-placeholder">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
          ) : (
            <img 
              src={articleImage || getRandomImage()}
              alt={article.title}
              onError={(e) => {
                e.target.src = getRandomImage();
              }}
            />
          )}
        </div>

        {/* Article Content */}
        <div className="mobile-news-detail-content">
          {/* Article Header */}
          <div className="mobile-news-detail-header">
            <h1 className="mobile-news-detail-title">
              {cleanJson(article.title)}
            </h1>
            
            <div className="mobile-news-detail-meta">
              <span className="mobile-news-detail-date">
                <i className="fas fa-calendar"></i>
                {formatDate(article.portalPublishDate)}
              </span>
              {article.weeklyCategory && (
                <span className={`mobile-news-detail-category ${getCategoryColor(article.weeklyCategory)}`}>
                  {article.weeklyCategory}
                </span>
              )}
            </div>
          </div>

          {/* Article Summary */}
          {article.summary && (
            <div className="mobile-news-detail-summary">
              <p>{cleanJson(article.summary)}</p>
            </div>
          )}

          {/* Article Body */}
          <div className="mobile-news-detail-body">
            {hasDescriptionContent(preprocessContentLinks(article.plainDescription || article.description || ''), article.title) && (
              <div className="mobile-news-content">
                {removeFirstBoldTag(addTargetBlankToLinks(cleanJson(preprocessContentLinks(article.plainDescription || article.description || ''))), article.title)}
              </div>
            )}
          </div>

          {/* Media Sections */}
          
          {/* PDF Section */}
          {article.file && typeof article.file === 'string' && article.file.trim() && (
            <div className="mobile-media-section mobile-pdf-section">
              <h3 className="mobile-media-title">
                <i className="fas fa-file-pdf"></i>
                Document
              </h3>
              {pdfSource ? (
                <div className="mobile-pdf-container">
                  <iframe
                    src={pdfSource}
                    title={`PDF: ${article.title}`}
                    className="mobile-pdf-iframe"
                  />
                </div>
              ) : (
                <div className="mobile-media-fallback">
                  <i className="fas fa-exclamation-triangle"></i>
                  {article.file.includes('https://') ? (
                    <>
                      This document cannot be embedded.
                      <br />
                      <a
                        href={article.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mobile-external-link"
                      >
                        <i className="fas fa-external-link-alt"></i> Open in new tab
                      </a>
                    </>
                  ) : (
                    'PDF is loading...'
                  )}
                </div>
              )}
            </div>
          )}

          {/* Video Section */}
          {article.video && typeof article.video === 'string' && article.video.trim() && (
            <div className="mobile-media-section mobile-video-section">
              <h3 className="mobile-media-title">
                <i className="fas fa-video"></i>
                Video
              </h3>
              {isVideoLoading ? (
                <div className="mobile-media-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading video...</span>
                </div>
              ) : (
                <div className={`mobile-video-container ${isVideoExpanded ? 'expanded' : ''}`}>
                  {isVideoEmbedded ? (
                    <iframe
                      src={videoSource}
                      title={article.title}
                      className="mobile-video-player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={videoSource}
                      controls
                      className="mobile-video-player"
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                      onEnded={() => setIsVideoPlaying(false)}
                      onError={(e) => {
                        console.error('Video error:', e);
                        setIsVideoPlaying(false);
                      }}
                    />
                  )}
                  <div className="mobile-video-controls">
                    <button 
                      className="mobile-video-expand-btn"
                      onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                    >
                      <i className={`fas fa-${isVideoExpanded ? 'compress' : 'expand'}`}></i>
                      {isVideoExpanded ? ' Compress' : ' Expand'}
                    </button>
                    {isVideoExpanded && (
                      <button 
                        className="mobile-video-close-btn"
                        onClick={() => setIsVideoExpanded(false)}
                      >
                        <i className="fas fa-times"></i>
                        Close
                      </button>
                    )}
                    {!isVideoEmbedded && (
                      <span className="mobile-video-status">
                        {isVideoPlaying ? 'Playing' : 'Paused'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Audio/Podcast Section */}
          {((article.podcast && typeof article.podcast === 'string' && article.podcast.trim()) || (article.audio && typeof article.audio === 'string' && article.audio.trim())) && (
            <div className="mobile-media-section mobile-audio-section">
              <h3 className="mobile-media-title">
                <i className="fas fa-podcast"></i>
                Audio
              </h3>
              {isAudioLoading ? (
                <div className="mobile-media-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading audio...</span>
                </div>
              ) : (
                <div className="mobile-audio-container">
                  {isPodcastEmbedded ? (
                    <iframe
                      src={podcastSource}
                      title={`Podcast: ${article.title}`}
                      className="mobile-podcast-player"
                      frameBorder="0"
                      allow="encrypted-media"
                      onLoad={() => setIsAudioLoading(false)}
                      onError={() => setIsAudioLoading(false)}
                    />
                  ) : (
                    <audio
                      className="mobile-audio-player"
                      src={podcastSource}
                      controls
                      onLoadStart={() => setIsAudioLoading(true)}
                      onCanPlay={() => setIsAudioLoading(false)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Back to News Button */}
          <div className="mobile-news-detail-footer">
            <button 
              className="mobile-back-to-news-btn"
              onClick={() => navigate('/mobile/news')}
            >
              <i className="fas fa-arrow-left"></i>
              Back to News
            </button>
          </div>
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileNewsDetail; 